<script>
	import { _ } from 'svelte-i18n'
	import Bugsnag from '@bugsnag/js'
	import UAParser from 'ua-parser-js'
	import { onMount } from 'svelte'
	import { detectFonts } from './modules/fontDetection'
	import {
		env, local_fonts, flash_content, extension_fonts, system_fonts, visits, font_detection_in_progress,
		chosen_theme, dark_theme, user, user_valid, detected_fonts, new_subscription_id, modals, hero_expanded, 
		inserts_enabled, buy_modal_initiator, previews_are_zoomed, dropzone_visible, previews_are_google_fonts } from './stores'
	import { logInUser, saveUserFontCount, pollSubscriptionId } from './services/user'
	import { isLoading } from 'svelte-i18n'
	import { getSubscriptionPrices } from './services/subscriptions'
	import LocaleSelector from './components/LocaleSelector.svelte'
	import Hero from './components/Hero.svelte'
	import Previews from './components/Previews.svelte'
	import Icons from './components/Icons.svelte'
	import Flash from './components/Flash.svelte'	
	import Modals from './components/Modals.svelte'
	import Footer from './components/Footer.svelte'
  import Dropzone from './components/Dropzone.svelte';

	const BUGSNAG_API_KEY = process.env.BUGSNAG_API_KEY

	if ($env === 'production') {
		Bugsnag.start(BUGSNAG_API_KEY)
	}

	onMount(() => {
		// clear document body background color css rule
		// this is only useful for first paint
		document.body.style.backgroundColor = '';

		// Update font_detection_in_progress to true
		$font_detection_in_progress = true
	})

	// Store new visit and send tutorial_begin event
	visits.new()
	// if (visits.first() && typeof window.gtag != "undefined") gtag("event", "tutorial_begin")
	// console.log(visits.first() ? 'Visits - first visit' : visits.second() ? 'Visits - second visit' : 'Visits - multiple (' + visits.count() + ')')

	const uaParser = new UAParser()
  const device = uaParser.getDevice()

	// 1. LOAD AND PARSE SYSTEM FONTS AND CHECK FOR EXTENSION OR LOCAL FONTS AND PARSE THEM
	const font_detection_promise = detectFonts()

	font_detection_promise.then(async () => { 

		// 2. SET DETECTED FONTS TO AVAILABLE FONTS
		if ($local_fonts) { $detected_fonts = $local_fonts; console.log('Fonts - Local - detected fonts are set to local fonts') }
		else if ($extension_fonts) { $detected_fonts = $extension_fonts; console.log('Fonts - Extension - detected fonts are set to extension fonts') }
		else { $detected_fonts = $system_fonts; console.log('Fonts - System - detected fonts are set to system fonts') }

		// Update font_detection_in_progress to false
		$font_detection_in_progress = false

		// 3. CHECK AND LOGIN USER FROM COOKIE + GET TAGS + MAP TAGS TO FONTS
		const login_result = await logInUser().catch(e => console.error(e))

		// 4. SAVE DETECTED FONT COUNT TO USER
		if (!!login_result) saveUserFontCount($detected_fonts.length)

		// GET SUBSCRIPTION PRICES FROM FASTSPRING
		// THIS WILL ALSO CHECK IF WE HAVE AN ONGOING SALE
		getSubscriptionPrices()
	})

	// KEEP HERO EXPANDED AS A CLASS ON BODY TO USE WITH HOTJAR
	$: if ($hero_expanded) {
		window.document.body.classList.add('hero_expanded')
	}
	else{
		window.document.body.classList.remove('hero_expanded')
		// Scroll to top after collapsing the hero on mobile
		if (device.type === 'mobile' || device.type === 'tablet') {
			window.scrollTo(0, 0)
		}
	}

	// KEEP ZOOMED STATE AS A CLASS ON BODY 
	// TO ENABLE SMOOTH SCROLLING (BREAKS SCROLLING IF ALWAYS ON)
	$: if ($previews_are_zoomed) {
		window.document.querySelector('html').classList.add('preview_zoomed')
	}
	else {
		window.document.querySelector('html').classList.remove('preview_zoomed')
	}

  // apply the chosen theme to dark_theme value, considering the auto theme value
  $: {
    if ($chosen_theme === 'auto') {
      const system_theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      console.log('Theme - Auto setting current theme to system theme: ', system_theme)
      // if system theme is dark, set dark_theme to true, else false
      $dark_theme = system_theme === 'dark'
    } else {
      console.log('Theme - Setting current theme to chosen theme: ', $chosen_theme)
      // if chosen theme is dark, set dark_theme to true, else false
      $dark_theme = $chosen_theme === 'dark'
    }
    // store chosen theme in local storage if user is valid
    if ($user_valid) {
      console.log('Theme - Storing theme in local storage')
      localStorage.setItem('theme', $chosen_theme)
    }
  }

  // apply theme dark_the$dark_theme to document body
  $: {
    if ($dark_theme) {
      document.body.classList.add('dark_theme')
      document.body.classList.remove('light_theme')
      console.log('Theme - Setting body class to dark theme')
    } else {
      document.body.classList.add('light_theme')
      document.body.classList.remove('dark_theme')
      console.log('Theme - Setting body class to light theme')
    }
  }

  // Listen to system theme changes and update if current setting is 'auto'
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', e => {
    if ($chosen_theme === 'auto') {
      const system_theme = e.matches ? 'dark' : 'light'
      console.log('Theme - Auto setting current theme to system theme: ', system_theme)
      // set dark_theme to true if system theme is dark, else false;
      $dark_theme = system_theme === 'dark'
    }
  });

	// USER LOGGED IN
	$: {
		if ($user !== undefined) { 
			$flash_content = $_('flashes.userLoggedIn', { values: { user_email: $user.email } })

			if($user_valid) {
				// Disable Ads
				$inserts_enabled = false

        // Get stored theme from local storage
        const storedTheme = localStorage.getItem('theme')
        if (storedTheme) $chosen_theme = storedTheme
        console.log('Theme - Setting user preffered theme from local storage: ', storedTheme)
				
				// enable printing from File>Print command
				window.document.body.classList.add('printable')

				// run hotjar customer survey
				if(typeof window.hj !== "undefined" && visits.count() > 5) {
					hj('stateChange', 'customersurvey')
				}
			}

			// store that the visit is from a customer
			visits.login()

		}
	}

	// RECEIVE FASTSPRING ORDER
	window.fsPurchaseCallback = callback_data => {
		console.log('Order - Callback received from popup', callback_data)
		$new_subscription_id = callback_data.items[0].subscription
		console.log("New subscription id: " + $new_subscription_id)
		modals.close()
		modals.open('activatingAccount')
		pollSubscriptionId()
		if(typeof window.hj !== "undefined" ) {
			hj('stateChange', 'purchased')
    }
		if (typeof window.gtag == "undefined") return
    gtag('event', 'purchase_complete', {
			'initiator': $buy_modal_initiator
    });
  }
	window.fsErrorCallback = (code, string) => {
		console.log("FS Popup Callback Error - Code:" + code + " Details:" + string)
		// Bugsnag here
  }
	window.fsPopupClosed = (data) => {
		console.log("FS Popup Closed:" + data)
		if (typeof window.gtag == "undefined") return
		if (data !== null) return
		if(typeof window.hj !== "undefined" ) {
			hj('stateChange', 'buymodalclosed')
    }
		gtag('event', 'fs_modal_closed');
  }

  // SHOW HOTJAR SURVEY TO 5TH TIME VISITORS
  // if(Number(visits.count()) >= 5 && Number(visits.count()) <= 10) {
  //   if(typeof window.hj !== "undefined" ) {
  //     hj('stateChange', 'pmfsurvey')
  //   }
  // }

	// SHOW DROPZONE ON DRAGENTER TO APP
	const preventDefaults = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }
	const handleDragEnter = (e) => {
    preventDefaults(e);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0 && !$previews_are_google_fonts) {
      $dropzone_visible = true;
    }
  }
</script>

<svelte:window on:dragenter={handleDragEnter} on:dragover={handleDragEnter}/>

<!-- svelte-ignore missing-declaration -->
<LocaleSelector />

	{#if $isLoading}
		Loading translation file...
	{:else}
		<Hero />
		<Previews/>
		<Footer/>
		<Modals/>
		<Flash/>
		<Dropzone/>
	{/if}

<Icons />
<div class="printProMessage">
	<p>Learn how to print previews of your fonts in our help page.</p>
	Click "Help" on top right and then "Printing fonts" from the list of contents.
</div>

<style>
	@import url('https://fonts.googleapis.com/css2?family=Raleway+Dots&display=swap');

	.printProMessage {
		display: none;
		padding: 2rem;
		font-size: 2.6rem;
		line-height: 3rem;
		font-family: var(--PensumSans-Bold);
		color: var(--c-text-faded);
	}
	.printProMessage p {
		margin-bottom: 2rem;
		font-size: 3.6rem;
		line-height: 4.8rem;
	}
</style>